"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { AlertTriangleIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { toProjectIdFromSaUrlGroupId } from "../shared/sa-url-group-id";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import type { DeleteSearchAnalyticsUrlGroupInput } from "./schema";

const DeleteSaUrlGroupConfirmQuery = graphql(`
  query DeleteSaUrlGroupConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on URLGroup {
        id
        name
        description
        op
        patterns {
          type
          pattern
        }
      }
    }
  }
`);

const ProjectDataQuery = graphql(`
  query SaUrlGroupDeleteConfirmProjectData($id: ID!) {
    node(id: $id) {
      __typename
      ... on SearchAnalyticsProject {
        name
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

/**
 * Search Analytics URL グループ削除の確認 UI を表示する。
 */
export function DeleteSearchAnalyticsUrlGroupConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const router = useRouter();
  const isTarget =
    isToolUIPart(part) &&
    getToolName(part) === "delete_search_analytics_url_group";
  const isInputAvailable = part.state === "input-available";
  const input = (part.input ??
    {}) as Partial<DeleteSearchAnalyticsUrlGroupInput>;
  const urlGroupId = input.urlGroupId ?? "";

  const [{ data, fetching }] = useQuery({
    query: DeleteSaUrlGroupConfirmQuery,
    variables: { id: urlGroupId },
    pause: !(isTarget && isInputAvailable && urlGroupId),
  });

  const projectIdFromInput = input.projectId ?? null;
  const projectIdDecoded =
    projectIdFromInput ||
    (urlGroupId ? toProjectIdFromSaUrlGroupId(urlGroupId) : null);
  const [{ data: projectData, fetching: projectFetching }] = useQuery({
    query: ProjectDataQuery,
    variables: { id: projectIdDecoded ?? "" },
    pause: !(isTarget && isInputAvailable && projectIdDecoded),
  });

  if (!isTarget || !isInputAvailable) return null;

  const groupNode = data?.node?.__typename === "URLGroup" ? data.node : null;
  const patternCount = groupNode?.patterns?.length ?? 0;

  return (
    <div className="p-4 text-sm space-y-4">
      <div>
        <div className="mb-2 font-medium">
          <Trans>Do you want to delete this Search Analytics URL group?</Trans>
        </div>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <Trans>Name</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-28" />
            ) : (
              <span className="font-medium">
                {groupNode?.name ?? input.name ?? (
                  <Trans>Unknown URL group</Trans>
                )}
              </span>
            )}
          </li>
          {groupNode?.description ? (
            <li>
              <Trans>Description</Trans>: {groupNode.description}
            </li>
          ) : null}
          {groupNode?.op ? (
            <li>
              <Trans>Logical operator</Trans>: {groupNode.op}
            </li>
          ) : null}
          <li>
            <Trans>Patterns</Trans>:{" "}
            {fetching ? <Skeleton className="h-4 w-16" /> : patternCount}
          </li>
          {projectIdDecoded ? (
            <li>
              <Trans>Project</Trans>:{" "}
              {projectFetching ? (
                <Skeleton className="h-4 w-24" />
              ) : (
                <span className="font-medium">
                  {projectData?.node?.__typename ===
                  "SearchAnalyticsProject" ? (
                    projectData.node.name
                  ) : (
                    <Trans>Unknown project</Trans>
                  )}
                </span>
              )}
            </li>
          ) : null}
        </ul>
      </div>
      <Alert variant="destructive" className="text-xs">
        <AlertTriangleIcon className="h-4 w-4" />
        <AlertTitle>
          <Trans>This action is irreversible</Trans>
        </AlertTitle>
        <AlertDescription>
          <Trans>
            Once deleted, this URL group cannot be recovered. Views or filters
            using this group will drop that condition and no longer narrow
            results.
          </Trans>
        </AlertDescription>
      </Alert>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
          variant="destructive"
          onSuccess={() => router.refresh()}
        >
          <Trans>Delete</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
